<script>
  import { createEventDispatcher } from 'svelte';
  import { openAgreementFile } from './agreementApi.js';

  export let requirement;
  export let busy = false;
  const dispatch = createEventDispatcher();
  let fileError = '';
  let openingFile = false;
  let showDocumentId = false;

  const statuses = {
    assigned: ['Laukia parašo', 'warning'],
    signing_in_progress: ['Pasirašymas pradėtas', 'info'],
    signed_storage_pending: ['Tikrinama ir saugoma', 'info'],
    finalization_failed: ['Patikra arba saugojimas nepavyko', 'error'],
    storage_failed: ['Nepavyko užbaigti', 'error'],
    provider_failed: ['Nepavyko užbaigti', 'error'],
    create_unknown: ['Tikrinama pasirašymo būsena', 'info'],
    declined: ['Pasirašyti atsisakyta', 'warning'],
    cancelled: ['Pasirašymas atšauktas', 'warning'],
    expired: ['Pasirašymo laikas baigėsi', 'warning'],
    completed_stored: ['Pasirašyta', 'success']
  };

  $: publicId = requirement.public_id || requirement.id;
  $: status = requirement.status || 'assigned';
  $: statusInfo = statuses[status] || ['Laukiama', 'warning'];
  $: complete = status === 'completed_stored';
  $: canSign = requirement.can_initiate === true && ['assigned', 'declined', 'cancelled', 'expired'].includes(status);
  $: canReconcile = requirement.can_reconcile === true && ['signing_in_progress', 'signed_storage_pending', 'create_unknown'].includes(status);
  $: canRetryFinalization = requirement.can_retry_finalization === true && status === 'finalization_failed';
  $: canRestartSigning = requirement.can_restart_signing === true && status === 'finalization_failed';
  $: canRestartPending = requirement.can_restart_pending === true && status === 'signing_in_progress';
  $: canResume = requirement.can_resume === true;
  $: canRestartLocal = requirement.can_restart_local === true;
  $: attemptOutcomeUnknown = requirement.attempt_outcome_unknown === true;
  $: title = requirement.title || requirement.version_title || 'Paslaugų teikimo sutartis';
  $: version = requirement.version_number || requirement.version;
  $: effectiveDate = requirement.effective_at || requirement.effective_date;
  $: completedDate = requirement.completed_at || requirement.signed_at;
  $: documentId = complete ? requirement.artifact_public_id : null;

  function formatDate(value) {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : new Intl.DateTimeFormat('lt-LT').format(date);
  }

  async function openFile(kind) {
    if (openingFile) return;
    openingFile = true;
    fileError = '';
    try { await openAgreementFile(publicId, kind); }
    catch (_) { fileError = 'Dokumento nepavyko atidaryti. Bandykite dar kartą.'; }
    finally { openingFile = false; }
  }
</script>

<article class="agreement-card" id={`agreement-${publicId}`} tabindex="-1">
  <div class="heading-row">
    {#if requirement?.family}<p>Šeimos sutartis: {requirement.family.name}. Pakanka vieno parašo visai šeimai.<br>{requirement.covered_students?.map(student => `${student.first_name} ${student.last_name}`).join(', ') || ''}</p>{/if}
    <h3>{title}</h3>
    <span class:success={statusInfo[1] === 'success'} class:info={statusInfo[1] === 'info'} class:error={statusInfo[1] === 'error'} class:warning={statusInfo[1] === 'warning'}>{statusInfo[0]}</span>
  </div>
  {#if complete && completedDate}
    <p class="meta">Pasirašyta {formatDate(completedDate)}</p>
  {:else if version || effectiveDate}
    <p class="meta">{version ? `Versija ${version}` : ''}{version && effectiveDate ? ' · ' : ''}{effectiveDate ? `galioja nuo ${formatDate(effectiveDate)}` : ''}</p>
  {/if}
  {#if ['signed_storage_pending', 'create_unknown'].includes(status)}
    <p class="progress" aria-live="polite">Tikrinama ir saugoma…</p>
  {/if}
  {#if canRestartLocal}<p class="inline-error" role="alert">Vietinio bandomojo pasirašymo duomenys prarasti, todėl šio bandymo tęsti negalima. Naujas bandymas neatšauks ankstesnio Dokobit bandymo.</p>{/if}
  {#if attemptOutcomeUnknown}<p class="inline-error" role="alert">Ankstesnio bandomojo bandymo rezultatas neaiškus. Naujo pasirašymo pradėti negalima, kol administratorius saugiai nepatikrino būsenos.</p>{/if}
  {#if status === 'finalization_failed'}<p class="inline-error" role="alert">Para&#353;as egzistuoja, ta&#269;iau jo kvalifikuotumo patikra nepavyko. Automatiniai bandymai sustabdyti. Pakartotin&#281; patikr&#261; galite prad&#279;ti tik ai&#353;kiu veiksmu.</p>{/if}
  {#if fileError}<p class="inline-error" role="alert">{fileError}</p>{/if}
  {#if openingFile}<p class="progress" role="status">Atidaromas dokumentas…</p>{/if}
  {#if complete && documentId}
    <p class="document-id">
      <button type="button" class="document-id-toggle" aria-expanded={showDocumentId} on:click={() => showDocumentId = !showDocumentId}>Rodyti dokumento ID</button>
      {#if showDocumentId}<span>{documentId}</span>{/if}
    </p>
  {/if}
  <div class="actions">
    <button type="button" class="secondary" disabled={openingFile} aria-busy={openingFile} on:click={() => openFile('source')} aria-label={`Peržiūrėti sutartį ${title}`}>{complete ? 'Peržiūrėti originalą' : 'Peržiūrėti sutartį'}</button>
    {#if complete}
      <button type="button" class="secondary" disabled={openingFile} aria-busy={openingFile} on:click={() => openFile('artifact')}>Atsisiųsti pasirašytą</button>
    {:else if canRetryFinalization || canRestartSigning}
      {#if canRetryFinalization}<button type="button" class="secondary" disabled={busy} aria-busy={busy} on:click={() => dispatch('reconcile', { requirement, retryFinalization: true })}>{busy ? 'Tikrinama…' : 'Pakartoti patikrą'}</button>{/if}
      {#if canRestartSigning}<button type="button" class="primary" disabled={busy} aria-busy={busy} on:click={(event) => dispatch('restartsigning', { requirement, trigger: event.currentTarget })}>Pasirašyti iš naujo</button>{/if}
    {:else if canResume}
      <button type="button" class="primary" disabled={busy} on:click={(event) => dispatch('resume', { requirement, trigger: event.currentTarget })}>Tęsti pasirašymą</button>
      {#if canRestartPending}<button type="button" class="secondary" disabled={busy} aria-busy={busy} on:click={(event) => dispatch('restartsigning', { requirement, trigger: event.currentTarget })}>Pradėti iš naujo</button>{/if}
    {:else if canRestartLocal}
      <button type="button" class="primary" on:click={() => dispatch('restartlocal', { requirement })}>Leisti naują bandomąjį bandymą</button>
    {:else if canSign}
      <button type="button" class="primary" on:click={(event) => dispatch('sign', { requirement, trigger: event.currentTarget })}>Pasirašyti Dokobit</button>
    {:else if canReconcile}
      <button type="button" class="secondary" disabled={busy} aria-busy={busy} on:click={() => dispatch('reconcile', { requirement })}>{busy ? 'Tikrinama…' : 'Atnaujinti būseną'}</button>
    {/if}
  </div>
</article>

<style>
  .agreement-card{border:1px solid #d1d5db;border-radius:8px;padding:16px;box-shadow:0 .5px 2px rgba(96,97,112,.16);background:#fff}.heading-row{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}.heading-row h3{font-size:17px;margin:2px 0;color:#333}.heading-row span{padding:5px 9px;border-radius:999px;font-size:13px;font-weight:600;white-space:nowrap}.success{color:#166534;background:#dcfce7}.warning{color:#854d0e;background:#fef3c7}.error{color:#b91c1c;background:#fee2e2}.info{color:#1e40af;background:#dbeafe}.meta,.progress{color:#666;margin:10px 0}.inline-error{color:#b91c1c;margin:10px 0}.document-id{color:#555;margin:10px 0;display:flex;gap:8px;align-items:center;flex-wrap:wrap;word-break:break-all}.document-id-toggle{border:0;background:transparent;color:#14695b;padding:0;text-decoration:underline;cursor:pointer;font:inherit}.document-id-toggle:focus{outline:2px solid #14695b;outline-offset:2px}.actions{display:flex;gap:10px;flex-wrap:wrap;margin-top:14px}.actions button{min-height:44px;border-radius:8px;padding:9px 14px;font-weight:600;cursor:pointer}.primary{background:#fab45a;border:1px solid #333;color:#111}.primary:hover{background:#c98f43}.secondary{background:white;border:1px solid #14695b;color:#14695b}.actions button:focus{outline:2px solid #14695b;outline-offset:2px}@media(max-width:767px){.heading-row{flex-direction:column}.actions{flex-direction:column}.actions button{width:100%}}@media(prefers-reduced-motion:reduce){*{transition:none!important}}
</style>
